import React, { FC, useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from '@hooks/redux-custom';
import { Product, ProductCategory } from '@models';
import { PACK_TYPES } from '@constants';
import { useGetAllProductCategoriesQuery } from '@services';
import { ProductFormSchema, schema } from './schema';
import BundleProductLists, { BundleProductList } from './BundleProductLists';
import { DeleteOutline } from '@mui/icons-material';
import { makeBundleSKU, getZoneId } from '@helpers';
import './ProductsForm.css';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, ContentState } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import htmlToDraft from 'html-to-draftjs';
import ReportingCategoryForm from './ReportingCategoryForm';

type Props = {
  onSubmit?: (formData: ProductFormSchema) => SubmitHandler<ProductFormSchema>;
  product: Product;
};

type CheckBoxProps = {
  disableCheckBox?: boolean;
};

export const ProductForm: FC<Props> = ({ onSubmit, product }) => {
  const [listProduct, setListProduct] = useState<BundleProductList[]>([]);
  const allProducts = useAppSelector((state) => state?.products?.value?.products);

  const brands = useAppSelector((state) => state?.product?.value?.brands);
  const { data: categories } = useGetAllProductCategoriesQuery();

  const zoneId = getZoneId(categories);

  const getCustomFieldByName = (fieldName: string) => product?.custom_fields?.find((field) => field.name === fieldName);

  const productsUpsell = allProducts
    ?.filter((product: Product) =>
      product?.custom_fields?.every(
        (field) => field.name !== 'is_bundle' || (field.name === 'is_bundle' && field.value.toLowerCase().trim() !== 'true'),
      ),
    )
    ?.map((product: Product) => {
      return {
        sku: product.sku,
        name: product.name,
      };
    });

  const bundleProductsUpsell = allProducts
    ?.filter((product: Product) =>
      product?.custom_fields?.find((field) => field.name === 'is_bundle' && field.value.toLowerCase().trim() === 'true'),
    )
    ?.map((product: Product) => {
      return {
        sku: product.sku,
        name: product.name,
      };
    });

  const getUpsellProducts = () => {
    const upsellProductSkus =
      getCustomFieldByName('upsell_products')
        ?.value?.split(',')
        .map((item) => item.trim())
        .filter((item) => item) ?? [];

    const upsellProducts = upsellProductSkus
      .map((sku: string) => allProducts.find((product: Product) => product.sku === sku))
      .filter((item) => item)
      .map((product: Product) => ({ sku: product.sku, name: product.name }));

    return upsellProducts;
  };

  const getUpsellBundleProducts = () => {
    const upsellBundlesSkus =
      getCustomFieldByName('upsell_bundles')
        ?.value?.split(',')
        .map((item) => item.trim())
        .filter((item) => item) ?? [];

    const upsellBundles = upsellBundlesSkus
      .map((sku: string) => allProducts.find((product: Product) => product.sku === sku))
      .filter((item) => item)
      .map((product: Product) => ({ sku: product.sku, name: product.name }));

    return upsellBundles;
  };

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm<ProductFormSchema>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const productDescription = getValues('description');

    if (productDescription) {
      const blocksFromHtml = htmlToDraft(productDescription);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      const contentToEditor = EditorState.createWithContent(contentState);

      setEditorState(contentToEditor);
    }
  }, [getValues]);

  const isBundle = watch('custom_fields.is_bundle');

  // Subset of categories - only the subcategories of Menu1.
  const [selectedCategories, setSelectedCategories] = useState<ProductCategory[]>(
    product?.categories?.reduce((productCategories, id) => {
      const category = categories?.find((c) => c.id === id && c.parent_id !== zoneId);
      return category ? [...productCategories, category] : productCategories;
    }, [] as ProductCategory[]),
  );

  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());

  const handleEditorChange = (state: any) => {
    setEditorState(state);
    convertContentToHTML();
  };

  const convertContentToHTML = () => {
    const currentContentAsHTML = convertToHTML(editorState.getCurrentContent());

    setValue('description', currentContentAsHTML);
  };

  // Subset of categories - only the subcategories of Zones.
  const [selectedZones, setSelectedZones] = useState<ProductCategory[]>(
    product?.categories?.reduce((zones, id) => {
      const zone = categories?.find((z) => z.id === id && z.parent_id === zoneId);
      return zone ? [...zones, zone] : zones;
    }, [] as ProductCategory[]),
  );

  const onSave = (productList: BundleProductList) => {
    const newListProduct = [...listProduct, productList];

    setListProduct(newListProduct);
  };

  const removeProductList = (sku: string) => {
    const list = listProduct.filter((item) => item.sku !== sku);

    setListProduct(list);
  };

  useEffect(() => {
    if (isBundle && !product.sku) {
      const sku = makeBundleSKU(allProducts);

      setValue('sku', sku);
    } else if (!product.sku) {
      setValue('sku', '');
    }
  }, [allProducts, isBundle, product.sku, setValue]);

  useEffect(() => {
    if (isBundle) {
      let totalCost = 0;
      let totalPrice = 0;

      if (listProduct.length > 0) {
        for (let i = 0; i < listProduct.length; i++) {
          totalCost += listProduct[i].cost * listProduct[i].quantity;
          totalPrice += listProduct[i].price * listProduct[i].quantity;
        }

        setValue('price', totalPrice);
        setValue('cost_price', totalCost);
      } else {
        setValue('price', totalPrice);
        setValue('cost_price', totalCost);
      }
    }
  }, [isBundle, listProduct, setValue]);

  useEffect(() => {
    if (product?.custom_fields) {
      const isBundle = product?.custom_fields?.find((field) => field.name === 'is_bundle') ?? { value: false };
      const productLists = product?.custom_fields?.filter((field) => field.name.includes('bundle_product_')) ?? { value: '' };

      if (Boolean(isBundle?.value) && productLists) {
        const parseItems = productLists.map((item) => {
          return JSON.parse(item.value);
        });

        const updatedListProducts = parseItems.map((prod: any) => ({
          productName: allProducts.find((alProd: { sku: string; name: string }) => alProd.sku === prod.sku)?.name ?? '',
          sku: prod.sku,
          quantity: prod.qty,
          price: prod.price,
          cost: allProducts.find((alProd: { sku: string; name: string }) => alProd.sku === prod.sku)?.cost_price ?? 0,
          weight: allProducts.find((alProd: { sku: string; name: string }) => alProd.sku === prod.sku)?.weight ?? 0,
        }));

        setListProduct(updatedListProducts);
      }
    }
  }, [product, allProducts]);

  const bundleCheckBoxViewRender = (props: CheckBoxProps) => {
    const { disableCheckBox } = props;
    return (
      <Grid container spacing={2} marginTop={1}>
        <Grid item xs={12}>
          <Controller
            control={control}
            defaultValue={getCustomFieldByName('is_bundle')?.value === 'true'}
            name={'custom_fields.is_bundle'}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={disableCheckBox}
                    defaultChecked={getCustomFieldByName('is_bundle')?.value === 'true'}
                    {...field}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 32 } }}
                  />
                }
                label="Bundle Product"
              />
            )}
          />
        </Grid>
      </Grid>
    );
  };

  const bundleCheckBoxRender = () => {
    // When a product is already bundled
    if (product.name && getCustomFieldByName('is_bundle')?.value === 'true') {
      return <>{bundleCheckBoxViewRender({ disableCheckBox: true })}</>;
    }
    // when product is added but is not bundled
    else if (
      (product.name && getCustomFieldByName('is_bundle')?.value === undefined) ||
      getCustomFieldByName('is_bundle')?.value === 'false'
    ) {
      return;
    }
    // when a new product is being added
    else if (!product.name && getCustomFieldByName('is_bundle')?.value === undefined) {
      return <>{bundleCheckBoxViewRender({ disableCheckBox: false })}</>;
    }
  };

  return (
    <form
      onSubmit={handleSubmit((data) => {
        const productData: any = data;

        if (!productData.custom_fields['algolia_tags'].length) {
          productData.custom_fields['algolia_tags'] = '';
        }

        if (productData.custom_fields['upsell_products']) {
          productData.custom_fields['upsell_products'] = productData.custom_fields['upsell_products']
            ?.map((item: { sku: string; name: string }) => item.sku)
            .join(', ');
        }

        if (productData.custom_fields['upsell_bundles']) {
          productData.custom_fields['upsell_bundles'] = productData.custom_fields['upsell_bundles']
            ?.map((item: { sku: string; name: string }) => item.sku)
            .join(', ');
        }

        if (data.custom_fields.is_bundle) {
          listProduct.map((prod, index) => {
            const stringIndex = (index + 1).toString();

            productData.custom_fields[`bundle_product_${stringIndex}`] = JSON.stringify({
              sku: prod.sku,
              price: prod.price,
              qty: prod.quantity,
            });

            return prod;
          });

          productData.custom_fields['bundle_SKU_count'] = listProduct.length;
        }

        if (onSubmit) {
          onSubmit(productData);
        }
      })}
      id="product-form"
    >
      <Typography variant="h6" marginBottom={1}>
        Basic Information
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Controller
            control={control}
            defaultValue={product?.is_visible}
            name={'is_visible'}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} defaultChecked={product?.is_visible} sx={{ '& .MuiSvgIcon-root': { fontSize: 32 } }} />}
                label="Visible on Storefront"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            defaultValue={getCustomFieldByName('display_menu_tile')?.value === 'true'}
            name={'custom_fields.display_menu_tile'}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    defaultChecked={getCustomFieldByName('display_menu_tile')?.value === 'true'}
                    sx={{ '& .MuiSvgIcon-root': { fontSize: 32 } }}
                    data-testid="product-form-menu-title-checkbox"
                  />
                }
                label="Visible on Home Page"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('name')}
            defaultValue={product?.name}
            disabled={isBundle && product.name !== undefined}
            fullWidth
            error={!!errors.name}
            helperText={errors.name?.message}
            label="Product Name *"
            data-testid="product-form-name-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.display_name')}
            defaultValue={getCustomFieldByName('display_name')?.value}
            error={!!errors.custom_fields?.display_name}
            helperText={errors.custom_fields?.display_name?.message}
            fullWidth
            label="Display Name *"
            data-testid="product-form-display-name-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.display_region')}
            defaultValue={getCustomFieldByName('display_region')?.value}
            fullWidth
            label="Region"
            data-testid="product-form-region-text"
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            defaultValue={getCustomFieldByName('brand')?.value ?? ''}
            name={'custom_fields.brand'}
            render={({ field }) => (
              <Autocomplete
                freeSolo
                fullWidth
                options={brands || []}
                {...field}
                onChange={(e, brand) => {
                  field.onChange(brand);
                }}
                renderInput={(params) => <TextField {...register('custom_fields.brand')} {...params} label="Brand" />}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            name={'custom_fields.algolia_tags'}
            defaultValue={getCustomFieldByName('algolia_tags')?.value?.split(',') ?? []}
            render={({ field }) => (
              <Autocomplete
                {...field}
                freeSolo
                multiple
                options={[]}
                clearOnBlur
                renderInput={(params) => {
                  return <TextField {...params} label="Algolia Tags" data-testid={`product-form-algolia-text`} />;
                }}
                data-testid="product-form-algolia-select"
                onChange={(event, data) => field.onChange(data)}
                value={field.value || []}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            defaultValue={selectedCategories}
            name={'categories'}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={categories?.filter((c) => c.parent_id !== zoneId) || []}
                getOptionLabel={(category) => category?.name}
                filterSelectedOptions
                {...field}
                onChange={(e, category) => {
                  field.onChange(category);
                  setSelectedCategories(category);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    key={params.id}
                    label="Categories *"
                    error={!!errors.categories}
                    helperText={errors.categories?.message}
                    data-testid={`${params.id}-product-form-categories-text`}
                  />
                )}
                data-testid="product-form-categories-select"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            defaultValue={selectedZones}
            name={'zones'}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={categories?.filter((c) => c.parent_id === zoneId) || []}
                getOptionLabel={(zone) => zone?.name}
                filterSelectedOptions
                {...field}
                onChange={(e, zone) => {
                  field.onChange(zone);
                  setSelectedZones(zone);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    key={params.id}
                    label="Zones *"
                    error={!!errors.zones}
                    helperText={errors.zones?.message}
                    data-testid={`${params.id}-product-form-zones-text`}
                  />
                )}
                data-testid="product-form-zones-select"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            InputLabelProps={{
              shrink: isBundle ? true : undefined,
            }}
            disabled={isBundle}
            {...register('sku')}
            error={!!errors.sku}
            helperText={errors.sku?.message}
            defaultValue={product?.sku}
            fullWidth
            label="SKU *"
            data-testid="product-form-sku-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.parent_product_sku')}
            defaultValue={getCustomFieldByName('parent_product_sku')?.value}
            fullWidth
            label="Parent Product SKU"
            data-testid="product-form-parent-product-sku"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('weight')}
            error={!!errors.weight}
            helperText={errors.weight?.message}
            InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
            defaultValue={product?.weight}
            fullWidth
            label="Weight *"
            data-testid="product-form-weight-text"
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            defaultValue={getUpsellProducts() ?? []}
            name={'custom_fields.upsell_products'}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                options={productsUpsell ?? []}
                getOptionLabel={(option) => `${option.name} - ${option.sku}`}
                filterSelectedOptions
                onChange={(e, newValue) => {
                  field.onChange(newValue);
                }}
                renderInput={(params) => <TextField {...params} key={params.id} label="Upsell Products" />}
                data-testid="product-form-categories-select"
                value={field.value || []}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            control={control}
            defaultValue={getUpsellBundleProducts() ?? []}
            name={'custom_fields.upsell_bundles'}
            render={({ field }) => (
              <Autocomplete
                {...field}
                multiple
                options={bundleProductsUpsell ?? []}
                getOptionLabel={(option) => `${option.name}-${option.sku}`}
                filterSelectedOptions
                onChange={(e, newValue) => {
                  field.onChange(newValue);
                }}
                renderInput={(params) => <TextField {...params} key={params.id} label="Upsell Bundles" />}
                data-testid="product-form-categories-select"
                value={field.value || []}
              />
            )}
          />
        </Grid>
      </Grid>
      <Typography variant="h6" marginBottom={2} marginTop={3}>
        Description
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Controller
            control={control}
            defaultValue={product?.description}
            name={'description'}
            render={() => {
              return (
                <Editor
                  editorState={editorState}
                  wrapperClassName="wrapper-class"
                  editorClassName="editor-class"
                  toolbarClassName="toolbar-class"
                  onEditorStateChange={handleEditorChange}
                />
              );
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.short_description')}
            error={!!errors?.custom_fields?.short_description}
            helperText={errors?.custom_fields?.short_description?.message}
            defaultValue={getCustomFieldByName('short_description')?.value}
            fullWidth
            label="Short Description *"
            data-testid="product-form-short-desc-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.display_tasting_tags')}
            defaultValue={getCustomFieldByName('display_tasting_tags')?.value}
            fullWidth
            label="Tastes Like"
            data-testid="product-form-tastes-like-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.display_volume')}
            defaultValue={getCustomFieldByName('display_volume')?.value}
            fullWidth
            label="Volume"
            data-testid="product-form-volume-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.display_alcoholic_content')}
            defaultValue={getCustomFieldByName('display_alcoholic_content')?.value}
            fullWidth
            label="Alcoholic Content (%)"
            data-testid="product-form-alcoholic-content-text"
          />
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="pack-type-label">Pack Type</InputLabel>
            <Select
              {...register('custom_fields.pack_type')}
              defaultValue={getCustomFieldByName('pack_type')?.value || ''}
              id="pack-type-select"
              label="Pack Type"
              labelId="pack-type-label"
              data-testid="product-form-pack-type-select"
            >
              {PACK_TYPES.map((packType: string) => (
                <MenuItem key={packType} value={packType}>
                  {packType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            inputProps={{ inputMode: 'numeric' }}
            {...register('custom_fields.standard_drink_count')}
            error={!!errors.custom_fields?.standard_drink_count}
            helperText={errors.custom_fields?.standard_drink_count?.message}
            defaultValue={getCustomFieldByName('standard_drink_count')?.value}
            fullWidth
            label="Standard Drink Count *"
            data-testid="product-form-standard-drink-count-text"
          />
        </Grid>
      </Grid>

      <Typography variant="h6" marginBottom={2} marginTop={3}>
        Marketing
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.modal_promo_banner_text')}
            defaultValue={getCustomFieldByName('modal_promo_banner_text')?.value}
            fullWidth
            label="Modal Promo Banner Text"
            data-testid="product-form-modal-promo-banner-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.display_badge')}
            defaultValue={getCustomFieldByName('display_badge')?.value}
            fullWidth
            label="Badge"
            data-testid="product-form-badge-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.menu_tile_promo_text')}
            defaultValue={getCustomFieldByName('menu_tile_promo_text')?.value}
            fullWidth
            label="Menu Tile Promo Text"
            data-testid="product-form-menu-title-promo-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            {...register('custom_fields.save_text')}
            defaultValue={getCustomFieldByName('save_text')?.value}
            fullWidth
            label="Save Text"
            data-testid="product-form-save-text"
          />
        </Grid>
      </Grid>
      <Typography variant="h6" marginBottom={2} marginTop={3}>
        SEO
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField {...register('page_title')} defaultValue={product?.page_title} fullWidth label="Page Title" />
        </Grid>
        <Grid item xs={6}>
          <TextField {...register('meta_description')} defaultValue={product?.meta_description} fullWidth label="Meta Description" />
        </Grid>
      </Grid>
      <Typography variant="h6" marginBottom={2} marginTop={3}>
        Pricing
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            disabled={isBundle}
            {...register('price')}
            error={!!errors.price}
            helperText={errors.price?.message}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            defaultValue={product?.price}
            fullWidth
            label="Price *"
            data-testid="product-form-price-text"
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            disabled={isBundle}
            {...register('cost_price')}
            error={!!errors.cost_price}
            helperText={errors.cost_price?.message}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            defaultValue={product?.cost_price}
            fullWidth
            label="Cost Price *"
            data-testid="product-form-cost-price-text"
          />
        </Grid>
      </Grid>

      {window.FEATURE_FLAGS.PRODUCT_REPORT_CAT_14 && (
        <ReportingCategoryForm errors={errors} register={register} getCustomFieldByName={getCustomFieldByName} />
      )}

      {bundleCheckBoxRender()}
      <Grid paddingTop={1}>
        {isBundle === true && (
          <>
            {listProduct.map((item: BundleProductList, index) => {
              const { productName, price, quantity, sku } = item;

              return (
                <Grid key={index} container spacing={2} padding={2}>
                  <Grid item xs={6} key={product.sku}>
                    <Typography data-testid="product-form-bundle-name-text">{productName}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography data-testid="product-form-bundle-price-qty-text">${price * quantity}</Typography>
                  </Grid>

                  <Grid item xs={2}>
                    <Typography data-testid="product-form-bundle-qty-text">{quantity}</Typography>
                  </Grid>
                  {/*Not showing delete when a product is already added and is bundled*/}
                  {!product.name && getCustomFieldByName('is_bundle')?.value === undefined ? (
                    <Grid item xs={2}>
                      <IconButton onClick={() => removeProductList(sku)} data-testid="product-form-bundle-remove-btn">
                        <DeleteOutline />
                      </IconButton>
                    </Grid>
                  ) : null}
                </Grid>
              );
            })}

            {/*Not showing bundle product editor when a product is already added and is bundled*/}
            {!product.name && getCustomFieldByName('is_bundle')?.value === undefined ? (
              <BundleProductLists selectedProducts={listProduct} onSave={onSave} />
            ) : null}
          </>
        )}
      </Grid>
    </form>
  );
};
